import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiStar, FiCamera } from "react-icons/fi";
import "./RatingsReview.css";
import { BASE_URL } from "../URL/constants";

const RatingsReview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { product, orderId } = location.state || {};

  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [description, setDescription] = useState("");
  const [title, setTitle] = useState("");
  const [image, setImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleStarClick = (value) => {
    setRating(value);
  };

  const handleStarHover = (value) => {
    setHoveredRating(value);
  };

  const handleStarLeave = () => {
    setHoveredRating(0);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      alert("Please select a rating");
      return;
    }
    if (!description.trim()) {
      alert("Please write a review description");
      return;
    }

    setSubmitting(true);
    try {
      // TODO: Implement API call to submit review
      const token = localStorage.getItem("authToken");
      const formData = new FormData();
      formData.append("product_id", product?.id || "");
      formData.append("order_id", orderId || "");
      formData.append("rating", rating);
      formData.append("description", description);
      formData.append("title", title);
      if (image) {
        formData.append("image", image);
      }

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      alert("Thank you for your review! Your feedback has been submitted.");
      navigate("/order");
    } catch (error) {
      console.error("Error submitting review:", error);
      alert("Failed to submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "/assets/images/products/iphone11.png";
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    if (imagePath.startsWith("/")) {
      return `${BASE_URL}${imagePath}`;
    }
    return `${BASE_URL}/assets/images/products/${imagePath}`;
  };

  return (
    <div className="ratings-review-container">
      <div className="ratings-review-header">
        <h1 className="ratings-review-title">Ratings & Reviews</h1>
        {product && (
          <div className="product-context">
            <img
              src={getImageUrl(product.image)}
              alt={product.name}
              className="product-thumbnail"
            />
            <span className="product-name-context">
              {product.name.length > 30
                ? `${product.name.substring(0, 30)}...`
                : product.name}
            </span>
          </div>
        )}
      </div>

      <div className="ratings-review-layout">
        {/* Left Sidebar - Guidelines */}
        <div className="review-guidelines">
          <div className="guideline-section">
            <h3 className="guideline-title">What makes a good review</h3>
            <p className="guideline-text">
              Your review should be about your experience with the product. Keep
              it honest and helpful for other shoppers.
            </p>
          </div>

          <div className="guideline-section">
            <h3 className="guideline-title">Why review a product?</h3>
            <p className="guideline-text">
              Your valuable feedback helps fellow shoppers decide. Share your
              honest experience to help others make informed choices.
            </p>
          </div>

          <div className="guideline-section">
            <h3 className="guideline-title">How to review a product?</h3>
            <p className="guideline-text">
              Include facts and honest opinions. For product/service issues,
              visit our{" "}
              <a href="#" className="help-link">
                help centre
              </a>
              .
            </p>
          </div>
        </div>

        {/* Right Main Content - Review Form */}
        <div className="review-form-container">
          <form onSubmit={handleSubmit} className="review-form">
            <div className="rating-section">
              <h3 className="form-section-title">Rate this product</h3>
              <div className="star-rating">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    className="star-button"
                    onClick={() => handleStarClick(value)}
                    onMouseEnter={() => handleStarHover(value)}
                    onMouseLeave={handleStarLeave}
                  >
                    <FiStar
                      className={`star-icon ${
                        value <= (hoveredRating || rating)
                          ? "star-filled"
                          : "star-outline"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="review-section">
              <h3 className="form-section-title">Review this product</h3>
              
              <div className="form-group">
                <label htmlFor="description" className="form-label">
                  Description
                </label>
                <textarea
                  id="description"
                  className="form-textarea"
                  placeholder="Description..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={6}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="title" className="form-label">
                  Title (optional)
                </label>
                <input
                  type="text"
                  id="title"
                  className="form-input"
                  placeholder="Review title..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="image-upload" className="image-upload-label">
                  <FiCamera className="camera-icon" />
                  <input
                    type="file"
                    id="image-upload"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="image-upload-input"
                  />
                </label>
                {image && (
                  <div className="image-preview">
                    <img
                      src={URL.createObjectURL(image)}
                      alt="Preview"
                      className="preview-image"
                    />
                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={() => setImage(null)}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => navigate("/order")}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="submit-btn"
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "SUBMIT"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RatingsReview;

